import { FullSimulationReport, SimulationGlobalAverage, SimulationResourceGlobalAverage, SimulationSendValues } from '../interfaces/Simulation';

export type AgentSimulationResultSource = 'EXECUTION' | 'IMPORT';
export type AgentSimulationResultFreshness = 'CURRENT' | 'STALE' | 'UNKNOWN';

export interface AgentSimulationTaskMetric {
  task_UUID: string;
  task_name: string;
  avg_arrivals?: number; avg_served?: number; avg_waiting_time?: number;
  avg_queue?: number; avg_wip?: number; avg_utilization?: number; avg_throughput?: number;
}
export interface AgentSimulationResourceMetric {
  resource_id: string; resource_name: string; avg_utilization?: number;
  avg_queue?: number; avg_waiting_time?: number; avg_throughput?: number;
}
export interface AgentSimulationResultDigest {
  completion: Record<string, number>;
  cycleTime: Record<string, number>;
  primaryBottleneck?: { task_UUID?: string; task_name?: string; rank?: number };
  primaryResourceBottleneck?: { resource_id?: string; resource_name?: string; rank?: number };
  tasks: AgentSimulationTaskMetric[];
  resources: AgentSimulationResourceMetric[];
  reportMarkdown?: string;
  files: { name: string; path: string | null }[];
}
export interface AgentSimulationLastRun {
  source: AgentSimulationResultSource;
  executedAt: string;
  executedSimulationRevision?: number;
  executedConfigurationFingerprint?: string;
  digest: AgentSimulationResultDigest;
}

const MAX_ROWS = 40;
const numberFields = ['created_total', 'finished_total', 'unfinished_total', 'finish_rate'];
const cycleFields = ['customers_finished', 'avg_cycle_time', 'min_cycle_time', 'max_cycle_time', 'p50', 'p90', 'p95'];
const pickNumbers = (value: unknown, fields: string[]): Record<string, number> => Object.fromEntries(
  fields.flatMap(field => typeof (value as Record<string, unknown> | undefined)?.[field] === 'number'
    ? [[field, (value as Record<string, number>)[field]]] : []));
const compactTask = (task: SimulationGlobalAverage): AgentSimulationTaskMetric => ({
  task_UUID: task.task_uuid, task_name: task.task_name,
  ...pickNumbers(task, ['avg_arrivals', 'avg_served', 'avg_waiting_time', 'avg_queue', 'avg_wip', 'avg_utilization', 'avg_throughput'])
});
const compactResource = (resource: SimulationResourceGlobalAverage): AgentSimulationResourceMetric => ({
  resource_id: resource.resource_id, resource_name: resource.resource_name,
  ...pickNumbers(resource, ['avg_utilization', 'avg_queue', 'avg_waiting_time', 'avg_throughput'])
});

export function simulationConfigurationFingerprint(config: SimulationSendValues): string {
  const canonical = (value: unknown): unknown => Array.isArray(value) ? value.map(canonical)
    : value && typeof value === 'object' ? Object.fromEntries(Object.keys(value as Record<string, unknown>).sort()
      .map(key => [key, canonical((value as Record<string, unknown>)[key])])) : value;
  const text = JSON.stringify(canonical(config));
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) { hash ^= text.charCodeAt(index); hash = Math.imul(hash, 16777619); }
  return `fnv1a-${(hash >>> 0).toString(16)}`;
}

export function buildAgentSimulationResultDigest(report: FullSimulationReport): AgentSimulationResultDigest {
  const result = report.simulation_result as unknown as Record<string, unknown>;
  const completion = pickNumbers(result['CUSTOMER_COMPLETION'], numberFields);
  const cycleTime = pickNumbers(result['CYCLE_TIME'], cycleFields);
  const tasks = Array.isArray(result['GLOBAL_AVERAGES']) ? (result['GLOBAL_AVERAGES'] as SimulationGlobalAverage[]).slice(0, MAX_ROWS).map(compactTask) : [];
  const resources = Array.isArray(result['RESOURCE_GLOBAL_AVERAGES']) ? (result['RESOURCE_GLOBAL_AVERAGES'] as SimulationResourceGlobalAverage[]).slice(0, MAX_ROWS).map(compactResource) : [];
  const primary = result['PRIMARY_BOTTLENECK'] as Record<string, unknown> | undefined;
  const primaryResource = result['PRIMARY_RESOURCE_BOTTLENECK'] as Record<string, unknown> | undefined;
  return {
    completion, cycleTime, tasks, resources,
    ...(primary ? { primaryBottleneck: { task_UUID: (primary['task_uuid'] ?? primary['task_UUID']) as string | undefined, task_name: primary['task_name'] as string | undefined, rank: primary['rank'] as number | undefined } } : {}),
    ...(primaryResource ? { primaryResourceBottleneck: { resource_id: primaryResource['resource_id'] as string | undefined, resource_name: primaryResource['resource_name'] as string | undefined, rank: primaryResource['rank'] as number | undefined } } : {}),
    ...(report.simulation_report ? { reportMarkdown: report.simulation_report.slice(0, 4000) } : {}),
    files: (report.other_files || []).slice(0, 20).map(file => ({ name: file.name, path: file.path }))
  };
}
